// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageStateByType } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { nullPage } from "@insite/client-framework/Store/Data/Pages/PagesState";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

export const xIsAuthenticated = (state: ApplicationState): boolean => {
    const session = getSession(state);
    return session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
};

export function getHeader(state: ApplicationState) {
    return getPageStateByType(state, "Header").value || nullPage;
}

export const getCartLinkWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Common/CartLink";
    })?.fields;
};

export const getHeaderActionsWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "HeaderActions/HeaderActions";
    })?.fields;
};

export const getHeaderLinkListWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/HeaderLinkList";
    })?.fields;
};

export const getHeaderSearchWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/HeaderSearchInput";
    })?.fields;
};

export const getHeaderSignInWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id)
        .reverse()
        .find((widget: WidgetProps) => {
            return widget.type === "Common/HeaderSignIn";
        })?.fields;
};

export const getHeaderSpotZone = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id)
        .reverse()
        .find((widget: WidgetProps) => {
            return widget.zone === "Spot";
        })?.fields;
};

export const getQuickOrderWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.zone === "QuickOrder" && widget.type === "HeaderIconLink/HeaderIconLink";
    });
};

export const getRfqWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.zone === "RFQ" && widget.type === "HeaderIconLink/HeaderIconLink";
    });
};

export const getSalesRepWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.zone === "SalesRep" && widget.type === "HeaderIconLink/HeaderIconLink";
    });
};

export const getLogoImageWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields;
};

export const getMainNavigationWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/MainNavigation";
    })?.fields;
};

export const getSecondaryNavigationWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/SecondaryNavigation";
    })?.fields;
};
